import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { NavigationProvider } from './user frontend/contexts/NavigationContext';
import { AdminProvider } from './contexts/AdminContext';
import { AuthProvider } from './user frontend/contexts/AuthContext';
import { WebsiteSettingsProvider } from './user frontend/contexts/WebsiteSettingsContext';
import ThemeInjector from './user frontend/components/Layout/ThemeInjector';
import ConditionalLayout from './user frontend/components/Layout/ConditionalLayout';
import AdminToWebsiteConnector from './components/AdminToWebsiteConnector';
import PortalFavicon from './components/PortalFavicon';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import { getSubdomain, getDefaultRoute } from './utils/subdomainDetector';

// Portal routers (lazy-loaded)
const AdminRouter = lazy(() => import('./admin/AdminRouter'));
const AgentRouter = lazy(() => import('./agent/AgentRouter'));
const CorporateRouter = lazy(() => import('./corporate/CorporateRouter'));

// Partner components (lazy-loaded)
const PartnerLogin = lazy(() => import('./partner/PartnerLogin'));const PartnerRegister = lazy(() => import('./partner/PartnerRegister'));
const PartnerForgotPassword = lazy(() => import('./partner/PartnerForgotPassword'));
const PartnerResetPassword = lazy(() => import('./partner/PartnerResetPassword'));
const PartnerLayout = lazy(() => import('./partner/PartnerLayout'));
const PartnerRegistrationLanding = lazy(() => import('./partner/PartnerRegistrationLanding'));
const HotelPartnerRegister = lazy(() => import('./partner/hotel/HotelPartnerRegister'));
const RestaurantPartnerRegister = lazy(() => import('./partner/restaurant/RestaurantPartnerRegister'));
const CarPartnerRegister = lazy(() => import('./partner/car/CarPartnerRegister'));
const BusPartnerRegister = lazy(() => import('./partner/bus/BusPartnerRegister'));
const HolidayPartnerRegister = lazy(() => import('./partner/holiday/HolidayPartnerRegister'));
const HotelPartnerLayout = lazy(() => import('./partner/hotel/HotelPartnerLayout'));
const HotelPartnerDashboard = lazy(() => import('./partner/hotel/HotelPartnerDashboard'));
const HotelManagement = lazy(() => import('./partner/hotel/HotelManagement'));
const RoomsManager = lazy(() => import('./partner/hotel/RoomsManager'));
const HotelPackagesARI = lazy(() => import('./partner/hotel/HotelPackagesARI'));
const RoomAvailability = lazy(() => import('./partner/hotel/RoomAvailability'));
const BookingManagement = lazy(() => import('./partner/hotel/BookingManagement'));
const HotelAnalytics = lazy(() => import('./partner/hotel/HotelAnalytics'));
const PartnerProfile = lazy(() => import('./partner/hotel/PartnerProfile'));
const PartnerSettings = lazy(() => import('./partner/hotel/PartnerSettings'));
const ChannelManagerIntegration = lazy(() => import('./partner/hotel/ChannelManagerIntegration'));
const HotelPayout = lazy(() => import('./partner/hotel/Payout'));
const HotelVerify = lazy(() => import('./partner/hotel/VerifyHotel'));
const HotelRecovery = lazy(() => import('./partner/hotel/HotelRecovery'));

// Bus partner components (lazy-loaded)
const BusPartnerLayout = lazy(() => import('./partner/bus/BusPartnerLayout'));
const BusPartnerVerify = lazy(() => import('./partner/bus/BusPartnerVerify'));
const BusPartnerDashboard = lazy(() => import('./partner/bus/BusPartnerDashboard'));
const BusManagement = lazy(() => import('./partner/bus/BusManagement'));
const BusRouteTimings = lazy(() => import('./partner/bus/BusRouteTimings'));
const BusSeatMapping = lazy(() => import('./partner/bus/BusSeatMapping'));
const BusPartnerInventory = lazy(() => import('./partner/bus/BusPartnerInventory'));
const BusBookings = lazy(() => import('./partner/bus/BusBookings'));
const BusAnalytics = lazy(() => import('./partner/bus/BusAnalytics'));
const BusPayout = lazy(() => import('./partner/bus/BusPayout'));
const BusProfile = lazy(() => import('./partner/bus/BusProfile'));

// User pages (lazy-loaded)
const HomePage = lazy(() => import('./user frontend/pages/HomePage'));
const FlightSearch = lazy(() => import('./user frontend/pages/flight/FlightSearch'));
const FlightSearchMobilePage = lazy(() => import('./user frontend/pages/flight/FlightSearchMobile'));
const FlightResults = lazy(() => import('./user frontend/pages/flight/FlightResults'));
const FlightRoundTripResults = lazy(() => import('./user frontend/pages/flight/FlightRoundTripResults'));
const FlightDetails = lazy(() => import('./user frontend/pages/flight/FlightDetails'));
const FlightTravellers = lazy(() => import('./user frontend/pages/flight/FlightTravellers'));
const FlightRoundTripDetails = lazy(() => import('./user frontend/pages/flight/FlightRoundTripDetails'));
const FlightRoundTripTravellers = lazy(() => import('./user frontend/pages/flight/FlightRoundTripTravellers'));
const FlightPayment = lazy(() => import('./user frontend/pages/flight/payment/Payment'));
const FlightPaymentSuccess = lazy(() => import('./user frontend/pages/flight/payment/PaymentSuccess'));
const TrainPayment = lazy(() => import('./user frontend/pages/train/payment/Payment'));
const TrainPaymentSuccess = lazy(() => import('./user frontend/pages/train/payment/PaymentSuccess'));
const HotelPayment = lazy(() => import('./user frontend/pages/hotel/payment/Payment'));
const HotelPaymentSuccess = lazy(() => import('./user frontend/pages/hotel/payment/PaymentSuccess'));
const RestaurantPayment = lazy(() => import('./user frontend/pages/restaurant/payment/Payment'));
const RestaurantPaymentSuccess = lazy(() => import('./user frontend/pages/restaurant/payment/PaymentSuccess'));
const HolidayPayment = lazy(() => import('./user frontend/pages/holiday/payment/Payment'));
const HolidayPaymentSuccess = lazy(() => import('./user frontend/pages/holiday/payment/PaymentSuccess'));
const HotelSearch = lazy(() => import('./user frontend/pages/hotel/HotelSearch'));
const HotelResults = lazy(() => import('./user frontend/pages/hotel/HotelResults'));
const HotelDetails = lazy(() => import('./user frontend/pages/hotel/HotelDetails'));
const HotelMedia = lazy(() => import('./user frontend/pages/hotel/HotelMedia'));
const HotelApiDetails = lazy(() => import('./user frontend/pages/hotel-api/HotelApiDetails'));
const HotelApiBooking = lazy(() => import('./user frontend/pages/hotel-api/HotelApiBooking'));
const HotelApiPayment = lazy(() => import('./user frontend/pages/hotel-api/HotelApiPayment'));
const BusSearch = lazy(() => import('./user frontend/pages/bus/BusSearch'));
const BusResults = lazy(() => import('./user frontend/pages/bus/BusResults'));
const BusSeatSelection = lazy(() => import('./user frontend/pages/bus/BusSeatSelection'));
const BusBooking = lazy(() => import('./user frontend/pages/bus/BusBooking'));
const BusPayment = lazy(() => import('./user frontend/pages/bus/payment/Payment'));
const BusPaymentSuccess = lazy(() => import('./user frontend/pages/bus/payment/PaymentSuccess'));
const TrainSearch = lazy(() => import('./user frontend/pages/train/TrainSearch'));
const TrainResults = lazy(() => import('./user frontend/pages/train/TrainResults'));
const TrainDetails = lazy(() => import('./user frontend/pages/train/TrainDetails'));
const TrainBooking = lazy(() => import('./user frontend/pages/train/TrainBooking'));
const HolidaySearch = lazy(() => import('./user frontend/pages/holiday/HolidaySearch'));
const HolidayResults = lazy(() => import('./user frontend/pages/holiday/HolidayResults'));
const HolidayDetails = lazy(() => import('./user frontend/pages/holiday/HolidayDetails'));
const CruiseSearch = lazy(() => import('./user frontend/pages/CruiseSearch'));
const Activities = lazy(() => import('./user frontend/pages/Activities'));
const Transfers = lazy(() => import('./user frontend/pages/car/Transfers'));
const CarResults = lazy(() => import('./user frontend/pages/car/CarResults'));
const CarDetails = lazy(() => import('./user frontend/pages/car/CarDetails'));
const CarPayment = lazy(() => import('./user frontend/pages/car/payment/Payment'));
const CarPaymentSuccess = lazy(() => import('./user frontend/pages/car/payment/PaymentSuccess'));
const RoomDetails = lazy(() => import('./user frontend/pages/hotel/RoomDetails'));
const HotelRoomBooking = lazy(() => import('./user frontend/pages/hotel/HotelRoomBooking'));
const RestaurantSearch = lazy(() => import('./user frontend/pages/restaurant/RestaurantSearch'));
const RestaurantResults = lazy(() => import('./user frontend/pages/restaurant/RestaurantResults'));
const RestaurantDetails = lazy(() => import('./user frontend/pages/restaurant/RestaurantDetails'));
const RestaurantBooking = lazy(() => import('./user frontend/pages/restaurant/RestaurantBooking'));
const RestaurantBill = lazy(() => import('./user frontend/pages/restaurant/RestaurantBill'));

const RestaurantPartnerLayout = lazy(() => import('./partner/restaurant/RestaurantPartnerLayout'));
const RestaurantPartnerDashboard = lazy(() => import('./partner/restaurant/RestaurantPartnerDashboard'));
const RestMgmt = lazy(() => import('./partner/restaurant/RestaurantManagement'));
const RestMenuManagement = lazy(() => import('./partner/restaurant/MenuManagement'));
const RestBookingManagement = lazy(() => import('./partner/restaurant/BookingManagement'));
const RestOrderManagement = lazy(() => import('./partner/restaurant/OrderManagement'));
const RestAnalytics = lazy(() => import('./partner/restaurant/Analytics'));
const RestReviewManagement = lazy(() => import('./partner/restaurant/ReviewManagement'));
const RestPartnerProfile = lazy(() => import('./partner/restaurant/PartnerProfile'));
const RestMenuUpload = lazy(() => import('./partner/restaurant/MenuUpload'));
const RestPackages = lazy(() => import('./partner/restaurant/PackageManagement'));
const RestOffers = lazy(() => import('./partner/restaurant/Offers'));
const RestPayout = lazy(() => import('./partner/restaurant/Payout'));
const RestVerifyRestaurant = lazy(() => import('./partner/restaurant/VerifyRestaurant'));
const MyProfile = lazy(() => import('./user frontend/pages/Profile/MyProfile'));
const MyTrips = lazy(() => import('./user frontend/pages/MyTrips/MyTrips'));
const Wallet = lazy(() => import('./user frontend/pages/TravelWallet'));
const Login = lazy(() => import('./user frontend/pages/Auth/Login'));
const Register = lazy(() => import('./user frontend/pages/Auth/Register'));
const ForgotPassword = lazy(() => import('./user frontend/pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./user frontend/pages/Auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./user frontend/pages/Auth/VerifyEmail'));
const AboutUs = lazy(() => import('./user frontend/pages/AboutUs'));
const ContactUs = lazy(() => import('./user frontend/pages/ContactUs'));
const TermsConditions = lazy(() => import('./user frontend/pages/TermsConditions'));
const PrivacyPolicy = lazy(() => import('./user frontend/pages/PrivacyPolicy'));
const FAQ = lazy(() => import('./user frontend/pages/FAQ'));
const CustomerSupport = lazy(() => import('./user frontend/pages/CustomerSupport'));
const BookingHelp = lazy(() => import('./user frontend/pages/BookingHelp'));
const CancellationPolicy = lazy(() => import('./user frontend/pages/CancellationPolicy'));
const RefundPolicy = lazy(() => import('./user frontend/pages/RefundPolicy'));
const TravelInsurance = lazy(() => import('./user frontend/pages/TravelInsurance'));
const GroupBookings = lazy(() => import('./user frontend/pages/GroupBookings'));
const ConnectionTest = lazy(() => import('./user frontend/pages/ConnectionTest'));
const Offers = lazy(() => import('./user frontend/pages/Offers'));
const AiSearch = lazy(() => import('./user frontend/pages/ai/AiSearch'));
const AiSearchResults = lazy(() => import('./user frontend/pages/ai/AiSearchResults'));

// Mobile-only route guard (kept here so the mobile search UI files stay untouched).
// Desktop visitors see a note + link instead of the app-style mobile page.
function MobileOnly({ children }) {
  const [isDesktop, setIsDesktop] = React.useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 761px)').matches : false
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 761px)');
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  if (!isDesktop) return children;
  return (
    <div style={{ maxWidth: 520, margin: '48px auto', padding: 24, textAlign: 'center', background: '#fff', borderRadius: 16, border: '1px solid #eee' }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Mobile-only search</h2>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
        This app-style search page is designed for mobile devices.
      </p>
      <Link to="/flights" style={{ display: 'inline-block', background: '#2563eb', color: '#fff', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
        Go to flight search
      </Link>
    </div>
  );
}

function App() {
  const [selectedPartnerType, setSelectedPartnerType] = React.useState('');

  React.useEffect(() => {
    const subdomain = getSubdomain();
    const currentPath = window.location.pathname;
    if (subdomain === 'admin' && !currentPath.startsWith('/admin')) {
      window.location.href = getDefaultRoute();
      return;
    }
    if (subdomain === 'partner' && !currentPath.startsWith('/partner')) {
      window.location.href = getDefaultRoute();
      return;
    }
  }, []);

  const renderPartnerRoutes = () => (
    <>
      <Route path="/partner/registaion" element={<PartnerLayout><PartnerRegistrationLanding /></PartnerLayout>} />
      <Route path="/partner/profile" element={<PartnerLayout><PartnerProfile /></PartnerLayout>} />
      <Route path="/partner/registaion/hotel" element={<PartnerLayout><HotelPartnerRegister /></PartnerLayout>} />
      <Route path="/partner/registaion/restaurant" element={<PartnerLayout><RestaurantPartnerRegister /></PartnerLayout>} />
      <Route path="/partner/registaion/car" element={<PartnerLayout><CarPartnerRegister /></PartnerLayout>} />
      <Route path="/partner/registaion/bus" element={<PartnerLayout><BusPartnerRegister /></PartnerLayout>} />
      <Route path="/partner/registaion/holiday" element={<PartnerLayout><HolidayPartnerRegister /></PartnerLayout>} />
      <Route path="/partner/login" element={<PartnerLayout><PartnerLogin selectedPartnerType={selectedPartnerType} onPartnerTypeChange={setSelectedPartnerType} /></PartnerLayout>} />
      <Route path="/partner/forgot-password" element={<PartnerLayout><PartnerForgotPassword /></PartnerLayout>} />
      <Route path="/partner/reset-password/:token" element={<PartnerLayout><PartnerResetPassword /></PartnerLayout>} />
      <Route path="/partner/hotel/login" element={<PartnerLayout><PartnerLogin fixedPartnerType="hotel" /></PartnerLayout>} />
      <Route path="/partner/hotel/forgot-password" element={<PartnerLayout><PartnerForgotPassword /></PartnerLayout>} />
      <Route path="/partner/hotel/reset-password/:token" element={<PartnerLayout><PartnerResetPassword /></PartnerLayout>} />
      <Route path="/partner/restaurant/login" element={<PartnerLayout><PartnerLogin fixedPartnerType="restaurant" /></PartnerLayout>} />
      <Route path="/partner/restaurant/forgot-password" element={<PartnerLayout><PartnerForgotPassword /></PartnerLayout>} />
      <Route path="/partner/restaurant/reset-password/:token" element={<PartnerLayout><PartnerResetPassword /></PartnerLayout>} />
      <Route path="/partner/car/login" element={<PartnerLayout><PartnerLogin fixedPartnerType="car" /></PartnerLayout>} />
      <Route path="/partner/car/forgot-password" element={<PartnerLayout><PartnerForgotPassword /></PartnerLayout>} />
      <Route path="/partner/car/reset-password/:token" element={<PartnerLayout><PartnerResetPassword /></PartnerLayout>} />
      <Route path="/partner/bus/login" element={<PartnerLayout><PartnerLogin fixedPartnerType="bus" /></PartnerLayout>} />
      <Route path="/partner/bus/forgot-password" element={<PartnerLayout><PartnerForgotPassword /></PartnerLayout>} />
      <Route path="/partner/bus/reset-password/:token" element={<PartnerLayout><PartnerResetPassword /></PartnerLayout>} />
      <Route path="/partner/holiday/login" element={<PartnerLayout><PartnerLogin fixedPartnerType="holiday" /></PartnerLayout>} />
      <Route path="/partner/holiday/forgot-password" element={<PartnerLayout><PartnerForgotPassword /></PartnerLayout>} />
      <Route path="/partner/holiday/reset-password/:token" element={<PartnerLayout><PartnerResetPassword /></PartnerLayout>} />
      <Route path="/partner/register" element={<PartnerLayout><PartnerRegister /></PartnerLayout>} />
      <Route path="/partner" element={<Navigate to="/partner/login" replace />} />
      <Route path="/partners" element={<Navigate to="/partner/login" replace />} />
      <Route path="/partner/hotel" element={<HotelPartnerLayout />}>
        <Route index element={<Navigate to="/partner/hotel/dashboard" replace />} />
        <Route path="dashboard" element={<HotelPartnerDashboard />} />
        <Route path="hotels" element={<HotelManagement />} />
        <Route path="rooms" element={<RoomsManager />} />
        <Route path="packages" element={<HotelPackagesARI mode="partner" />} />
        <Route path="availability" element={<RoomAvailability />} />
        <Route path="bookings" element={<BookingManagement />} />
        <Route path="analytics" element={<HotelAnalytics />} />
        <Route path="channel-manager" element={<ChannelManagerIntegration />} />
        <Route path="profile" element={<PartnerProfile />} />
        <Route path="recovery" element={<HotelRecovery />} />
        <Route path="settings" element={<PartnerSettings />} />
        <Route path="payout/settlement" element={<HotelPayout view="settlement" />} />
        <Route path="payout/history" element={<HotelPayout view="history" />} />
        <Route path="payout/withdrawal" element={<HotelPayout view="withdrawal" />} />
        <Route path="payout/method" element={<HotelPayout view="method" />} />
        <Route path="payout/tax" element={<HotelPayout view="tax" />} />
        <Route path="verify" element={<HotelVerify />} />
      </Route>
      <Route path="/partner/restaurant" element={<RestaurantPartnerLayout />}>
        <Route index element={<Navigate to="/partner/restaurant/dashboard" replace />} />
        <Route path="dashboard" element={<RestaurantPartnerDashboard />} />
        <Route path="manage" element={<RestMgmt />} />
        <Route path="menu" element={<RestMenuUpload />} />
        <Route path="packages" element={<RestPackages />} />
        <Route path="offers" element={<RestOffers />} />
        <Route path="bookings" element={<RestBookingManagement />} />
        <Route path="payout/settlement" element={<RestPayout view="settlement" />} />
        <Route path="payout/history" element={<RestPayout view="history" />} />
        <Route path="payout/withdrawal" element={<RestPayout view="withdrawal" />} />
        <Route path="payout/method" element={<RestPayout view="method" />} />
        <Route path="verify" element={<RestVerifyRestaurant />} />
        <Route path="analytics" element={<RestAnalytics />} />
        <Route path="reviews" element={<RestReviewManagement />} />
        <Route path="profile" element={<RestPartnerProfile />} />
      </Route>
      <Route path="/partner/bus" element={<BusPartnerLayout />}>
        <Route index element={<Navigate to="/partner/bus/dashboard" replace />} />
        <Route path="verify" element={<BusPartnerVerify />} />
        <Route path="dashboard" element={<BusPartnerDashboard />} />
        <Route path="buses" element={<BusManagement />} />
        <Route path="routes" element={<BusRouteTimings />} />
        <Route path="seat-mapping" element={<BusSeatMapping />} />
        <Route path="inventory" element={<BusPartnerInventory />} />
        <Route path="bookings" element={<BusBookings />} />
        <Route path="analytics" element={<BusAnalytics />} />
        <Route path="payout" element={<BusPayout />} />
        <Route path="profile" element={<BusProfile />} />
      </Route>
    </>
  );

  const renderUserRoutes = () => (
    <>
      <Route path="/" element={<HomePage />} />
      {/* Home search-tab URLs — same homepage, tab preselected (URL-only change) */}
      <Route path="/d-hotel" element={<HomePage />} />
      <Route path="/d-flight" element={<HomePage />} />
      <Route path="/d-train" element={<HomePage />} />
      <Route path="/d-restaurant" element={<HomePage />} />
      <Route path="/d-bus" element={<HomePage />} />
      <Route path="/d-car" element={<HomePage />} />
      <Route path="/d-holiday" element={<HomePage />} />
      <Route path="/flights" element={<FlightSearch />} />
      <Route path="/flights/m-search" element={<MobileOnly><FlightSearchMobilePage /></MobileOnly>} />
      <Route path="/flights/results" element={<FlightResults />} />
      <Route path="/flights/roundtrip-results" element={<FlightRoundTripResults />} />
      <Route path="/flights/booking" element={<FlightDetails />} />
      <Route path="/flights/roundtrip-booking" element={<FlightRoundTripDetails />} />
      <Route path="/flights/:id" element={<FlightDetails />} />
      <Route path="/flights/travellers" element={<FlightTravellers />} />
      <Route path="/flights/roundtrip-travellers" element={<FlightRoundTripTravellers />} />
      <Route path="/flights/payment" element={<FlightPayment />} />
      <Route path="/flight/payment" element={<FlightPayment />} />
      <Route path="/flight/payment/success" element={<FlightPaymentSuccess />} />
      <Route path="/flights/payment/success" element={<FlightPaymentSuccess />} />
      <Route path="/hotels" element={<HotelSearch />} />
      <Route path="/hotels/results" element={<HotelResults />} />
      <Route path="/hotels/payment" element={<HotelPayment />} />
      <Route path="/hotels/payment/success" element={<HotelPaymentSuccess />} />
      <Route path="/hotels/:hotelId" element={<HotelDetails />} />
      <Route path="/hotels/:hotelId/media" element={<HotelMedia />} />
      <Route path="/hotels/:hotelId/rooms/:roomId" element={<RoomDetails />} />
      <Route path="/hotels/:hotelId/rooms/:roomId/book" element={<HotelRoomBooking />} />
      <Route path="/hotel-details-api/book" element={<HotelApiBooking />} />
      <Route path="/hotel-details-api/payment" element={<HotelApiPayment />} />
      <Route path="/hotel-details-api/:hotelId" element={<HotelApiDetails />} />
      <Route path="/restaurants" element={<RestaurantSearch />} />
      <Route path="/restaurants/results" element={<RestaurantResults />} />
      <Route path="/restaurants/payment" element={<RestaurantPayment />} />
      <Route path="/restaurants/payment/success" element={<RestaurantPaymentSuccess />} />
      <Route path="/restaurants/:restaurantId/book" element={<RestaurantBooking />} />
      <Route path="/restaurants/:restaurantId/bill" element={<RestaurantBill />} />
      <Route path="/restaurants/:restaurantId" element={<RestaurantDetails />} />
      <Route path="/buses" element={<BusSearch />} />
      <Route path="/buses/results" element={<BusResults />} />
      <Route path="/buses/select-seat" element={<BusSeatSelection />} />
      <Route path="/buses/:id/select-seat" element={<BusSeatSelection />} />
      <Route path="/buses/booking" element={<BusBooking />} />
      <Route path="/buses/payment" element={<BusPayment />} />
      <Route path="/buses/payment/success" element={<BusPaymentSuccess />} />
      <Route path="/buses/confirmation" element={<Navigate to="/my-trips" replace />} />
      <Route path="/trains" element={<TrainSearch />} />
      <Route path="/trains/results" element={<TrainResults />} />
      <Route path="/trains/booking" element={<TrainBooking />} />
      <Route path="/trains/payment" element={<TrainPayment />} />
      <Route path="/trains/payment/success" element={<TrainPaymentSuccess />} />
      <Route path="/trains/:trainId" element={<TrainDetails />} />
      <Route path="/holidays" element={<HolidaySearch />} />
      <Route path="/holidays/results" element={<HolidayResults />} />
      <Route path="/holidays/payment" element={<HolidayPayment />} />
      <Route path="/holidays/payment/success" element={<HolidayPaymentSuccess />} />
      <Route path="/holidays/details" element={<HolidayDetails />} />
      <Route path="/holidays/:id" element={<HolidayDetails />} />
      <Route path="/cruise" element={<CruiseSearch />} />
      <Route path="/activities" element={<Activities />} />
      <Route path="/transfers" element={<Transfers />} />
      <Route path="/transfers/results" element={<CarResults />} />
      <Route path="/transfers/:carId" element={<CarDetails />} />
      <Route path="/transfers/payment" element={<CarPayment />} />
      <Route path="/transfers/payment/success" element={<CarPaymentSuccess />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/agent/*" element={<AgentRouter />} />
      <Route path="/corporate/*" element={<CorporateRouter />} />
      <Route path="/dashboard" element={<Navigate to="/my-profile" replace />} />
      <Route path="/my-profile" element={<MyProfile />} />
      <Route path="/wallet" element={<Wallet />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/connection-test" element={<ConnectionTest />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/blog" element={<div className="container mx-auto px-4 py-8"><h1 className="text-3xl font-bold mb-4">Blog</h1><p>Travel blog coming soon...</p></div>} />
      <Route path="/support" element={<CustomerSupport />} />
      <Route path="/help" element={<BookingHelp />} />
      <Route path="/cancellation" element={<CancellationPolicy />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/insurance" element={<TravelInsurance />} />
      <Route path="/group-booking" element={<GroupBookings />} />
      <Route path="/offers" element={<Offers />} />
      <Route path="/ai-search" element={<AiSearch />} />
      <Route path="/ai-search/results" element={<AiSearchResults />} />
      <Route path="/my-trips" element={<MyTrips />} />
      <Route path="/hourly-stays" element={<div className="container mx-auto px-4 py-8"><h1 className="text-3xl font-bold mb-4">Hourly Stays</h1><p>Book hourly hotel stays for short visits...</p></div>} />
      <Route path="/airport-cabs" element={<div className="container mx-auto px-4 py-8"><h1 className="text-3xl font-bold mb-4">Airport Cabs</h1><p>Reliable airport transfer services...</p></div>} />
      <Route path="/intercity-cabs" element={<div className="container mx-auto px-4 py-8"><h1 className="text-3xl font-bold mb-4">Intercity Cabs</h1><p>Long distance cab services between cities...</p></div>} />
      <Route path="/travel-insurance" element={<TravelInsurance />} />
    </>
  );

  const websiteType = process.env.REACT_APP_WEBSITE_TYPE || 'user';

  return (
    <Router>
      <AuthProvider>
        <NavigationProvider>
          <AdminProvider>
            <WebsiteSettingsProvider>
              <ThemeInjector />
              <PortalFavicon />
              <AdminToWebsiteConnector />
              <div className="min-h-screen bg-gray-50">
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                  <ChunkErrorBoundary>
                  {websiteType === 'user' && (
                    <ConditionalLayout>
                      <main className="min-h-screen">
                        <Routes>
                          {renderPartnerRoutes()}
                          {renderUserRoutes()}
                          <Route path="/admin/*" element={<AdminRouter />} />
                          <Route path="*" element={
                            <div className="container mx-auto px-4 py-16 text-center">
                              <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
                              <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
                              <Link to="/" className="inline-block px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Go to Homepage</Link>
                            </div>
                          } />
                        </Routes>
                      </main>
                    </ConditionalLayout>
                  )}
                  {websiteType === 'admin' && (
                    <Routes>
                      <Route path="/admin/*" element={<AdminRouter />} />
                      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                    </Routes>
                  )}
                  {websiteType === 'partner' && (
                    <Routes>
                      {renderPartnerRoutes()}
                      <Route path="*" element={<Navigate to="/partner/login" replace />} />
                    </Routes>
                  )}
                  {websiteType === 'agent' && <AgentRouter />}
                  {websiteType === 'corporate' && <CorporateRouter />}
                  </ChunkErrorBoundary>
                </Suspense>
                <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
              </div>
            </WebsiteSettingsProvider>
          </AdminProvider>
        </NavigationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
